<template>
  <div v-if="canSetSequentialCompletionReq" class="course-plan-activity-sequential-access">
    <input-checkbox
      :id="uniqueCheckboxId"
      kind="white"
      value="sequentialActivityCompletionPrerequisite"
      :selected-values="sequentialActivityCompletionPrerequisite"
      :disabled="disableSequentialCompletionReq"
      @change="handleSelect"
    >
      {{ t("course_plan_sequential_activity_access_label") }}
    </input-checkbox>

    <cv-tooltip
      direction="bottom"
      :alignment="tooltipAlignment"
      :tip="sequentialActivityAccessTooltip"
    >
      <svg-icon name="Info32" kind="indicative" />
    </cv-tooltip>

    <fetch-data-button
      class="btn-save"
      kind="primary"
      v-bind="dataSubmitButton"
      :disabled="dataSubmitButton.disabled || disableSequentialCompletionReq"
      @click="handleSubmit"
      @reset-error-state="handleSubmitButtonResetError"
    >
      <span> {{ t("button_apply") }} </span>
    </fetch-data-button>
  </div>
  <div v-else-if="showPreviousCompletionReqLabel" class="course-plan-activity-sequential-access">
    <span class="icon">
      <svg-icon name="Lock24" kind="indicative" />
    </span>
    <span class="text">{{ t("course_plan_sequential_activity_access_label") }}</span>
    <cv-tooltip
      direction="bottom"
      :alignment="tooltipAlignment"
      :tip="sequentialActivityAccessTooltip"
    >
      <svg-icon name="Info32" kind="indicative" />
    </cv-tooltip>
  </div>
</template>

<script setup lang="ts">
import { EVENT } from "../course.constant";
import store from "./CoursePlanStore";
import DeviceUtils from "@utils/device.utils";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

// Props
interface Props {
  course: Record<string, unknown>;
  section: Record<string, unknown>;
  editable?: boolean;
}

const { course, section, editable = false } = defineProps<Props>();

// Emits
const emit = defineEmits<{
  event: [data: unknown];
}>();

// Composables
const { t } = useI18n();

// Data
const dataSubmitButton = ref({
  kind: "primary",
  size: "small",
  loading: false,
  error: false,
  disabled: true,
});
const sequentialActivityCompletionPrerequisite = ref(
  section.sequentialActivityCompletionReq ? ["sequentialActivityCompletionPrerequisite"] : [],
);

// Computed
const sequentialActivityCompletionReq = computed(() => {
  return section.sequentialActivityCompletionReq;
});

const canSetSequentialCompletionReq = computed(() => {
  return editable && store.features().setPrerequisites;
});

const disableSequentialCompletionReq = computed(() => {
  return section.activityData.length < 1;
});

const tooltipAlignment = computed(() => {
  return DeviceUtils.isMobile() ? "end" : "center";
});

const sequentialActivityAccessTooltip = computed(() => {
  return t("course_plan_sequential_activity_access_tooltip_content");
});

const showPreviousCompletionReqLabel = computed(() => {
  return !editable && sequentialActivityCompletionReq.value;
});

const uniqueCheckboxId = computed(() => {
  return `${section.id}-${String(Math.random())}`;
});

// Methods
const handleSubmit = () => {
  emit("event", {
    type: EVENT.SAVE_SEQUENTIAL_ACTIVITY_COMPLETION_PREREQUISITE,
    data: {
      lessonUuid: course.uuid,
      sectionId: section.id,
      isSequentialActivityCompletionPre: sequentialActivityCompletionPrerequisite.value.includes(
        "sequentialActivityCompletionPrerequisite",
      ),
    },
    event: {
      onSuccess: () => {
        dataSubmitButton.value.loading = false;
        dataSubmitButton.value.disabled = true;
      },
      onFail: () => {
        dataSubmitButton.value.loading = false;
      },
      onAction: () => {
        dataSubmitButton.value.loading = true;
      },
      onRevert: () => {
        dataSubmitButton.value.disabled = true;
        sequentialActivityCompletionPrerequisite.value = sequentialActivityCompletionReq.value
          ? ["sequentialActivityCompletionPrerequisite"]
          : [];
      },
    },
  });
};

const handleSubmitButtonResetError = () => {
  dataSubmitButton.value.error = false;
};

const handleSelect = (selectValue) => {
  if (
    sequentialActivityCompletionReq.value &&
    selectValue.includes("sequentialActivityCompletionPrerequisite")
  ) {
    dataSubmitButton.value.disabled = true;
  } else if (
    sequentialActivityCompletionReq.value &&
    !selectValue.includes("sequentialActivityCompletionPrerequisite")
  ) {
    dataSubmitButton.value.disabled = false;
  } else {
    dataSubmitButton.value.disabled = !(
      !sequentialActivityCompletionReq.value &&
      selectValue.includes("sequentialActivityCompletionPrerequisite")
    );
  }
  sequentialActivityCompletionPrerequisite.value = selectValue.includes(
    "sequentialActivityCompletionPrerequisite",
  )
    ? ["sequentialActivityCompletionPrerequisite"]
    : [];
};
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.course-plan-activity-sequential-access {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: $spacing-03;
  margin: $spacing-05 $spacing-03 $spacing-05 0;

  .input-checkbox {
    margin: 0;

    .text {
      @include carbon--type-style("body-short-01");
    }
  }

  .icon {
    display: inline-flex;
  }
}
</style>
